import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { getUserUsage } from '@/api/avpulse-api'
import { PLAN_NAMES_MAP } from '@/constants/plans'

interface UserPlanContextType {
  currentPlan: string | null
  isLoading: boolean
  isFreePlan: boolean
  planDisplayName: string | null
  refresh: () => Promise<void>
}

const UserPlanContext = createContext<UserPlanContextType | undefined>(
  undefined,
)

export function UserPlanProvider({ children }: { children: React.ReactNode }) {
  const { user, isLoading: isAuthLoading } = useAuth()
  const [currentPlan, setCurrentPlan] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  const fetchPlanData = useCallback(async () => {
    if (!user) {
      setCurrentPlan(null)
      return
    }

    setIsLoading(true)
    try {
      const usageData = await getUserUsage()
      if (usageData) {
        setCurrentPlan(usageData.plan)
      }
    } catch (error) {
      console.error('Failed to fetch plan data:', error)
      setCurrentPlan(null)
    } finally {
      setIsLoading(false)
    }
  }, [user])

  // Fetch plan data when user changes
  useEffect(() => {
    if (user && !isAuthLoading) {
      fetchPlanData()
    } else if (!user) {
      setCurrentPlan(null)
    }
  }, [user, isAuthLoading, fetchPlanData])

  const isFreePlan =
    !currentPlan ||
    PLAN_NAMES_MAP[currentPlan as keyof typeof PLAN_NAMES_MAP] ===
      PLAN_NAMES_MAP.free

  const planDisplayName = currentPlan
    ? PLAN_NAMES_MAP[currentPlan as keyof typeof PLAN_NAMES_MAP] || currentPlan
    : null

  const value: UserPlanContextType = {
    currentPlan,
    isLoading: isLoading || isAuthLoading,
    isFreePlan,
    planDisplayName,
    refresh: fetchPlanData,
  }

  return (
    <UserPlanContext.Provider value={value}>
      {children}
    </UserPlanContext.Provider>
  )
}

export function useUserPlan() {
  const context = useContext(UserPlanContext)
  if (context === undefined) {
    throw new Error('useUserPlan must be used within a UserPlanProvider')
  }
  return context
}
